import clsx from 'clsx';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { EvaluateMergeRuleResult } from '@gitbook/app-merge-rules';
import { Icon } from '@gitbook/spine-icons';
import { type IconDefinition, renderIcon, Skeleton } from '@gitbook/spine-react';

import { useEvaluateMergeRules } from '../../MergeRules';
import { ChangeRequestMergeButtons } from '../ChangeRequestMergeButtons';
import { WithChangeRequestRevision } from '../Changes';

type ChangeRequestMergeBlockProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
};

export function ChangeRequestMergeBlock(props: ChangeRequestMergeBlockProps) {
    const { space, changeRequest } = props;

    return (
        <WithChangeRequestRevision space={space} changeRequest={changeRequest}>
            <React.Suspense fallback={<ChangeRequestMergeBlockFallback />}>
                <ChangeRequestMergeBlockWithData {...props} />
            </React.Suspense>
        </WithChangeRequestRevision>
    );
}

function ChangeRequestMergeBlockFallback() {
    return (
        <div className="w-full rounded border">
            <div className="p-4">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <Skeleton className="h-[1.375rem] w-1/3" />
                    <Skeleton className="h-5 w-1/2" />
                </div>
            </div>
        </div>
    );
}

function ChangeRequestMergeBlockWithData(props: ChangeRequestMergeBlockProps) {
    const { space, changeRequest } = props;
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });

    const state = React.useMemo(() => {
        return getChangeRequestMergeBlockState({
            mergeRulesEvaluation,
        });
    }, [mergeRulesEvaluation]);

    return (
        <div
            className={clsx('w-full rounded border ', {
                'border-warning bg-warning': state.status === 'blocked',
            })}
        >
            <div className="p-4">
                <ChangeRequestMergeState {...props} state={state} />
            </div>
        </div>
    );
}

function ChangeRequestMergeState(
    props: ChangeRequestMergeBlockProps & { state: ChangeRequestMergeBlockState }
) {
    const { space, changeRequest, analyticsContext, state } = props;

    const title = <div className="font-medium text-ui-heading-small">{state.title}</div>;

    const description = state.description ? (
        <div className="text-muted text-ui-base">{state.description}</div>
    ) : null;

    if (state.status === 'blocked') {
        return (
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    <ChangeRequestMergeStatusIcon
                        icon={Icon.X}
                        className="border border-hint-warning text-warning"
                    />
                    {title}
                </div>
                {description}
                <MergeRulesMessages rules={state.rules} />
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                {title}
                {description}
            </div>

            <ChangeRequestMergeButtons
                space={space}
                changeRequest={changeRequest}
                analyticsContext={analyticsContext}
            />
        </div>
    );
}

function ChangeRequestMergeStatusIcon(props: { icon: IconDefinition; className: string }) {
    const { icon, className } = props;
    return (
        <div
            className={clsx(
                'flex shrink-0 items-center justify-center rounded-full p-px',
                className
            )}
        >
            {renderIcon(icon, { size: 12 })}
        </div>
    );
}

function MergeRulesMessages(props: { rules: EvaluateMergeRuleResult[] }): React.ReactNode {
    const { rules } = props;

    if (rules.length === 0) {
        return null;
    }

    return (
        <ul
            className={clsx(
                'list-inside space-y-1 text-ui-base',
                rules.length === 1 ? 'list-none pl-0.5' : 'list-disc pl-1.5'
            )}
        >
            {rules.map((rule) => (
                <li key={rule.rule.rule}>{rule.message}</li>
            ))}
        </ul>
    );
}

type ChangeRequestMergeBlockState = {
    status: 'blocked' | 'ready';
    title: string;
    description?: string;
    rules: EvaluateMergeRuleResult[];
};

const READY_TO_MERGE_STATE: ChangeRequestMergeBlockState = {
    title: 'Ready to merge',
    description: 'Merging is allowed on this change request',
    status: 'ready',
    rules: [],
};

const APPROVED_READY_TO_MERGE_STATE: ChangeRequestMergeBlockState = {
    title: 'Changes approved',
    description: 'Merging is allowed on this change request',
    status: 'ready',
    rules: [],
};

function getReadyToMergeState(hasApprovedReview: boolean): ChangeRequestMergeBlockState {
    return hasApprovedReview ? APPROVED_READY_TO_MERGE_STATE : READY_TO_MERGE_STATE;
}

function getChangeRequestMergeBlockState(input: {
    mergeRulesEvaluation: ReturnType<typeof useEvaluateMergeRules>;
}): ChangeRequestMergeBlockState {
    const { mergeRulesEvaluation } = input;
    const reviews = mergeRulesEvaluation.context.reviews;

    if (mergeRulesEvaluation.result === 'rejected') {
        return {
            title: 'Merge rules failed',
            status: 'blocked',
            rules: mergeRulesEvaluation.rejectedRules,
        };
    }

    const hasApprovedReview = reviews.some(
        (review) => review.status === api.ChangeRequestReviewStatus.Approved
    );

    return getReadyToMergeState(hasApprovedReview);
}
